import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import KpiCard from '../../components/KpiCard';
import Header from '../../components/Header';
import IkDashboardWidget from '../../components/IkDashboardWidget';
import { styles, colors, radius, shadows } from '../../styles/theme';
import useCachedQuery from '../../hooks/useCachedQuery';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import { searchMatchPre } from '../../utils/searchMatch';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const formatTL = (val) => {
  if (val == null) return '-';
  if (val >= 1000000) return `${(val / 1000000).toFixed(1)}M TL`;
  if (val >= 1000) return `${Math.round(val).toLocaleString('tr-TR')} TL`;
  return `${Number(val).toLocaleString('tr-TR')} TL`;
};

const StatusBadge = ({ status }) => {
  const style = styles.statusColors[status] || { bg: '#e9ecef', color: '#495057' };
  return (
    <span style={{
      display: 'inline-block',
      padding: '4px 12px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: 'bold',
      background: style.bg,
      color: style.color
    }}>
      {status}
    </span>
  );
};

const MuhasebeDashboard = () => {
  useAuth();
  const [arama, setArama] = useState('');
  const debouncedArama = useDebouncedValue(arama, 300);

  const {
    data: faturalar = [],
    isLoading,
    isValidating,
    revalidate: revalidateFaturalar
  } = useCachedQuery(
    ['dashboard', 'muhasebe', 'faturalar'],
    async () => {
      const res = await fetch(`${API_URL}/api/faturalar`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      return Array.isArray(data) ? data : [];
    },
    { staleTime: 20000 }
  );

  const {
    data: özet = {},
    isLoading: ozetLoading,
    revalidate: revalidateOzet
  } = useCachedQuery(
    ['dashboard', 'muhasebe', 'özet'],
    async () => {
      const res = await fetch(`${API_URL}/api/raporlar/özet`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return await res.json();
    },
    { staleTime: 20000 }
  );

  // Auto-refresh her 30 saniye
  const refreshAll = useCallback(() => {
    revalidateFaturalar({ force: true }).catch(() => {});
    revalidateOzet({ force: true }).catch(() => {});
  }, [revalidateFaturalar, revalidateOzet]);

  useEffect(() => {
    const interval = setInterval(refreshAll, 30000);
    return () => clearInterval(interval);
  }, [refreshAll]);

  const filteredFaturalar = useMemo(() => {
    const q = (debouncedArama || '').trim().toLowerCase();
    if (!q) return faturalar;
    return faturalar.filter(f =>
      searchMatchPre(f.fatura_no, q) ||
      searchMatchPre(f.tedarikçi, q) ||
      searchMatchPre(f.durum, q)
    );
  }, [faturalar, debouncedArama]);

  const loading = isLoading || ozetLoading;

  return (
    <div>
      <Header title="Muhasebe Panosu" />
      <div style={{ padding: '20px', background: colors.background, minHeight: 'calc(100vh - 60px)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '30px' }}>
          <KpiCard title="Odenmemis Fatura" value={loading ? '...' : faturalar.filter(f => f.durum === 'ODENECEK').length} color={styles.danger} icon="document" />
          <KpiCard title="Haftalık Ödeme" value={loading ? '...' : formatTL(özet.haftalik_odeme)} color={styles.warning} icon="card" />
          <KpiCard title="Cari Bakiye" value={loading ? '...' : formatTL(özet.cari_bakiye)} color={styles.primary} icon="bank" />
          <KpiCard title="Ay Gideri" value={loading ? '...' : formatTL(özet.ay_gideri)} color={styles.info} icon="chart" />
        </div>

        {/* İK Özet */}
        <div style={{ marginBottom: 20 }}>
          <IkDashboardWidget />
        </div>

        {/* Hızlı İşlemler */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '12px',
          marginBottom: '24px'
        }}>
          {[
            { label: 'Fatura Girişi', icon: '🧾', route: '/finans#fatura-girisi', color: '#1565c0' },
            { label: 'Gider Girişi', icon: '📝', route: '/finans#gider-girisi', color: '#e65100' },
            { label: 'Cari Hesaplar', icon: '🏦', route: '/finans#cari-hesaplar', color: '#2e7d32' },
            { label: 'Üretim', icon: '🏭', route: '/uretim', color: '#7b1fa2' },
            { label: 'Transfer', icon: '🚚', route: '/stok-depo#inventory', color: '#00838f' },
            { label: 'Ödeme Planı', icon: '💳', route: '/finans#odeme-plani', color: '#c62828' },
          ].map(item => (
            <div
              key={item.label}
              onClick={() => { window.location.href = item.route; }}
              style={{
                background: colors.surface,
                borderRadius: radius.xl,
                padding: '20px 16px',
                textAlign: 'center',
                cursor: 'pointer',
                borderLeft: `4px solid ${item.color}`,
                boxShadow: shadows.sm,
                transition: 'transform 0.15s, box-shadow 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.12)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = shadows.sm; }}
            >
              <div style={{ fontSize: '28px', marginBottom: '8px' }}>{item.icon}</div>
              <div style={{ fontSize: '14px', fontWeight: '600', color: item.color }}>{item.label}</div>
            </div>
          ))}
        </div>

        <div style={{ background: colors.surface, borderRadius: radius.lg, padding: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', gap: '12px', flexWrap: 'wrap' }}>
            <h3 style={{ margin: 0, color: colors.textPrimary, fontSize: '15px', fontWeight: 600, letterSpacing: '-0.01em' }}>Son Faturalar</h3>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <input
                value={arama}
                onChange={(e) => setArama(e.target.value)}
                placeholder="Fatura no / tedarikçi ara"
                style={{ width: '240px', maxWidth: '100%', border: '1px solid #ddd', borderRadius: '6px', padding: '8px 10px', fontSize: '13px' }}
              />
              {isValidating && !isLoading && (
                <span style={{ fontSize: '12px', color: colors.textSecondary, background: colors.backgroundAlt, borderRadius: '12px', padding: '4px 10px' }}>
                  Güncelleniyor...
                </span>
              )}
            </div>
          </div>

          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: colors.backgroundAlt }}>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>Fatura No</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>Tedarikçi</th>
                <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>Tutar</th>
                <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>KDV</th>
                <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>Toplam</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>Vade</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>Durum</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: 6 }).map((_, row) => (
                  <tr key={`skeleton-${row}`} style={{ borderBottom: `1px solid ${colors.border}` }}>
                    {Array.from({ length: 7 }).map((__, col) => (
                      <td key={`skeleton-${row}-${col}`} style={{ padding: '12px 16px' }}>
                        <div style={{ height: '12px', borderRadius: '4px', background: colors.border, width: col === 1 ? '85%' : '65%' }} />
                      </td>
                    ))}
                  </tr>
                ))
              ) : filteredFaturalar.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ padding: '24px 10px', textAlign: 'center', color: colors.textSecondary }}>
                    Kayit bulunamadı.
                  </td>
                </tr>
              ) : filteredFaturalar.map(fatura => (
                <tr key={fatura.id} style={{ borderBottom: `1px solid ${colors.border}` }}>
                  <td style={{ padding: '12px 16px', fontWeight: 'bold' }}>{fatura.fatura_no}</td>
                  <td style={{ padding: '12px 16px' }}>{fatura.tedarikçi}</td>
                  <td style={{ padding: '12px 16px', textAlign: 'right' }}>{(fatura.tutar || 0).toLocaleString('tr-TR')} TL</td>
                  <td style={{ padding: '12px 16px', textAlign: 'right' }}>{(fatura.kdv || 0).toLocaleString('tr-TR')} TL</td>
                  <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 'bold' }}>{(fatura.toplam || 0).toLocaleString('tr-TR')} TL</td>
                  <td style={{ padding: '12px 16px' }}>{fatura.vade}</td>
                  <td style={{ padding: '12px 16px' }}><StatusBadge status={fatura.durum} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MuhasebeDashboard;
